import React, { useState, useEffect, useCallback } from "react";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import {
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";
import { fetchData, postData } from "../../services/apiServices";
import debounce from "lodash/debounce";

const UsersTradeData = () => {
  const [modal, setModal] = useState(false);
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState("all");
  const [pending, setPending] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    username: "",
  });
  const [viewModal, setViewModal] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);

  const openViewModal = (row) => {
    setSelectedTrade(row);
    setViewModal(true);
  };
  const columns = [
    {
      name: "User",
      cell: (row) => (
        <div>
          <div>
            <strong>{row.user?.username || row.user?.phonenumber}</strong>
          </div>
          <small className="text-muted">{row.user?.email || "-"}</small>
        </div>
      ),
    },
    {
      name: "Match",
      cell: (row) => (
        <div>
          <div>
            {row.match?.home_team} vs {row.match?.away_team}
          </div>
          <small className="text-muted">{row.match?.sport_title}</small>
        </div>
      ),
    },
    {
      name: "Market",
      selector: (row) => row.market,
    },
    {
      name: "Type",
      cell: (row) => (
        <span
          className={`badge ${row.type === "back" ? "bg-success" : "bg-danger"}`}
        >
          {row.type}
        </span>
      ),
    },
    {
      name: "Odds",
      selector: (row) => row.odds,
    },
    {
      name: "Stake",
      selector: (row) => `₹${row.stake}`,
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`badge ${
            row.status === "matched"
              ? "bg-primary"
              : row.status === "partial"
                ? "bg-warning text-dark"
                : row.status === "settled"
                  ? "bg-success"
                  : "bg-secondary"
          }`}
        >
          {row.status}
        </span>
      ),
    },
    {
      name: "Placed At",
      selector: (row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      name: "Action",
      cell: (row) => (
        <Button size="sm" color="info" onClick={() => openViewModal(row)}>
          View
        </Button>
      ),
    },
  ];

  const getData = async (page, searchQuery = "") => {
    setPending(true);
    try {
      // Pass search query as a parameter object
      const params = {
        page,
        limit: perPage,
        search: searchQuery,
      };

      const response = await fetchData("api/admin/getAllTradeHistory", params);
      if (response && response.status && response.data) {
        let sortedData = response.data;
        if (searchQuery) {
          sortedData = sortedData.sort((a, b) => {
            const aUsername = (a.username || "").toLowerCase();
            const bUsername = (b.username || "").toLowerCase();
            const aEmail = (a.email || "").toLowerCase();
            const bEmail = (b.email || "").toLowerCase();
            const searchLower = searchQuery.toLowerCase();

            // Exact matches come first
            if (aUsername === searchLower || aEmail === searchLower) return -1;
            if (bUsername === searchLower || bEmail === searchLower) return 1;

            // Then starts with matches
            if (
              aUsername.startsWith(searchLower) ||
              aEmail.startsWith(searchLower)
            )
              return -1;
            if (
              bUsername.startsWith(searchLower) ||
              bEmail.startsWith(searchLower)
            )
              return 1;

            // Then includes matches
            const aIncludes =
              aUsername.includes(searchLower) || aEmail.includes(searchLower);
            const bIncludes =
              bUsername.includes(searchLower) || bEmail.includes(searchLower);
            if (aIncludes && !bIncludes) return -1;
            if (!aIncludes && bIncludes) return 1;

            return 0;
          });
        }

        setRows(sortedData);
        setTotalRows(response.pagination?.totalRecords || 0);
      } else {
        setRows([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setRows([]);
      setTotalRows(0);
    } finally {
      setPending(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      getData(1, searchQuery);
      setCurrentPage(1);
    }, 300), // Reduced debounce time for better responsiveness
    [perPage],
  );

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    debouncedSearch(searchValue);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    getData(page, search);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPending(true);
    setPerPage(newPerPage);
    try {
      const params = {
        page,
        limit: newPerPage,
        search,
      };
      const response = await fetchData("api/admin/getAllTradeHistory", params);
      if (response && response.data) {
        setRows(response.data);
        setTotalRows(response.pagination.totalUsers);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setPending(false);
    }
  };

  const handleUserTypeChange = (e) => {
    setUserType(e.target.value);
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setNewUser({ email: "", password: "", username: "" });
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await postData("api/user/createusers", newUser);
      toggleModal();
      getData(currentPage, search);
    } catch (error) {
      console.error("Failed to create user:", error);
    }
  };

  useEffect(() => {
    getData(1, "");
  }, []);

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex gap-3 align-items-center">
            <Input
              type="search"
              placeholder="Search users..."
              value={search}
              onChange={handleSearch}
              style={{ width: "250px" }}
            />
            {/* <Input
              type="select"
              value={userType}
              onChange={handleUserTypeChange}
              style={{ width: "150px" }}
            >
              <option value="all">All Users</option>
              <option value="internal">Internal</option>
              <option value="external">External</option>
            </Input> */}
          </div>
          {/* <Button color="primary" onClick={toggleModal}>
            Create User
          </Button> */}
        </div>

        <DataTable
          columns={columns}
          data={rows}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={pending}
          responsive
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 30, 50, 100]}
        />

        <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
          <ModalHeader toggle={() => setViewModal(false)}>
            Trade Details
          </ModalHeader>

          <ModalBody>
            {selectedTrade && (
              <div className="container-fluid">
                <Row className="mb-2">
                  <Col md={6}>
                    <b>User:</b>{" "}
                    {selectedTrade.user?.username ||
                      selectedTrade.user?.phonenumber}
                  </Col>
                  <Col md={6}>
                    <b>Email:</b> {selectedTrade.user?.email || "-"}
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={12}>
                    <b>Match:</b> {selectedTrade.match?.home_team} vs{" "}
                    {selectedTrade.match?.away_team}
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={6}>
                    <b>Sport:</b> {selectedTrade.match?.sport_title}
                  </Col>
                  <Col md={6}>
                    <b>Match Status:</b> {selectedTrade.match?.status}
                  </Col>
                </Row>

                <hr />

                <Row className="mb-2">
                  <Col md={6}>
                    <b>Market:</b> {selectedTrade.market}
                  </Col>
                  <Col md={6}>
                    <b>Type:</b> {selectedTrade.type}
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={6}>
                    <b>Odds:</b> {selectedTrade.odds}
                  </Col>
                  <Col md={6}>
                    <b>Stake:</b> ₹{selectedTrade.stake}
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={6}>
                    <b>Remaining Stake:</b> ₹{selectedTrade.remainingStake}
                  </Col>
                  <Col md={6}>
                    <b>Status:</b> {selectedTrade.status}
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={6}>
                    <b>Placed At:</b>{" "}
                    {new Date(selectedTrade.createdAt).toLocaleString()}
                  </Col>
                  <Col md={6}>
                    <b>Matched At:</b>{" "}
                    {selectedTrade.matchedAt
                      ? new Date(selectedTrade.matchedAt).toLocaleString()
                      : "-"}
                  </Col>
                </Row>
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={() => setViewModal(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
};

export { UsersTradeData };
